import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTier } from "../utils/tierContext";
import { useUser } from "../utils/userContext";
import { isOwner as checkIsOwner } from "../utils/access";
import styles from "./SubscriptionBanner.module.css";

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function SubscriptionBanner() {
  const { user } = useUser();
  const {
    tier,
    subscriptionStatus,
    currentPeriodEnd,
    canceledAt,
    gracePeriodEnd,
    loading,
    error,
  } = useTier();

  const [dismissed, setDismissed] = useState<string | null>(null);

  const isOwner = checkIsOwner(Number(user?.roleId));

  // Only owners can act on tenant billing issues.
  if (!isOwner || loading) return null;

  // Fail-closed warning — tier context had an error loading
  if (error && dismissed !== "error") {
    return (
      <div className={`${styles.banner} ${styles["banner-info"]}`}>
        <span className={styles["banner-icon"]}>⚠️</span>
        <span className={styles["banner-text"]}>
          Unable to verify your subscription. Some features may be restricted.
        </span>
        <button
          type="button"
          className={styles["banner-dismiss"]}
          onClick={() => setDismissed("error")}
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    );
  }

  // Free tier — no subscription banners needed
  if (tier === "free_crew") return null;

  // Past due — payment failed, grace period active
  if (subscriptionStatus === "past_due" && dismissed !== "past_due") {
    return (
      <div className={`${styles.banner} ${styles["banner-warning"]}`}>
        <span className={styles["banner-icon"]}>⚠️</span>
        <span className={styles["banner-text"]}>
          Your payment is past due.
          {gracePeriodEnd
            ? ` Update your payment method by ${formatDate(gracePeriodEnd)} to avoid being downgraded to FreeCrew.`
            : " Update your payment method to avoid being downgraded to FreeCrew."}
          {" "}
          <Link to="/settings/billing" className={styles["banner-link"]}>
            Manage Billing
          </Link>
        </span>
        <button
          type="button"
          className={styles["banner-dismiss"]}
          onClick={() => setDismissed("past_due")}
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    );
  }

  if (subscriptionStatus === "unpaid" && dismissed !== "unpaid") {
    return (
      <div className={`${styles.banner} ${styles["banner-danger"]}`}>
        <span className={styles["banner-icon"]}>🚫</span>
        <span className={styles["banner-text"]}>
          Your subscription is unpaid.{" "}
          <Link to="/settings/billing" className={styles["banner-link"]}>
            Manage Billing
          </Link>
        </span>
        <button
          type="button"
          className={styles["banner-dismiss"]}
          onClick={() => setDismissed("unpaid")}
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    );
  }

  if (subscriptionStatus === "incomplete" && dismissed !== "incomplete") {
    return (
      <div className={`${styles.banner} ${styles["banner-warning"]}`}>
        <span className={styles["banner-icon"]}>⚠️</span>
        <span className={styles["banner-text"]}>
          Subscription checkout is incomplete.{" "}
          <Link to="/settings/billing" className={styles["banner-link"]}>
            Finish Checkout
          </Link>
        </span>
        <button
          type="button"
          className={styles["banner-dismiss"]}
          onClick={() => setDismissed("incomplete")}
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    );
  }

  if (
    subscriptionStatus === "incomplete_expired" &&
    dismissed !== "incomplete_expired"
  ) {
    return (
      <div className={`${styles.banner} ${styles["banner-warning"]}`}>
        <span className={styles["banner-icon"]}>⚠️</span>
        <span className={styles["banner-text"]}>
          Your checkout session expired before payment completed.{" "}
          <Link to="/settings/billing" className={styles["banner-link"]}>
            Start Checkout
          </Link>
        </span>
        <button
          type="button"
          className={styles["banner-dismiss"]}
          onClick={() => setDismissed("incomplete_expired")}
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    );
  }

  // Canceled but still has access until period end
  if (
    subscriptionStatus === "canceled" &&
    currentPeriodEnd &&
    dismissed !== "canceled"
  ) {
    return (
      <div className={`${styles.banner} ${styles["banner-danger"]}`}>
        <span className={styles["banner-icon"]}>🚫</span>
        <span className={styles["banner-text"]}>
          Your subscription was canceled
          {canceledAt && ` on ${formatDate(canceledAt)}`}. Access continues
          until {formatDate(currentPeriodEnd)}.{" "}
          <Link to="/settings/billing" className={styles["banner-link"]}>
            Resubscribe
          </Link>
        </span>
        <button
          type="button"
          className={styles["banner-dismiss"]}
          onClick={() => setDismissed("canceled")}
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    );
  }

  return null;
}
